/**
 * Homepage banner slider — accessible carousel (WAI-ARIA APG + WCAG 2.2).
 * Stacked active slide. Safari-safe muted inline video playback.
 * Tag: <zoh-banner>
 */

class ZohBanner extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {number} */
  #index = 0;

  /** @type {number | null} */
  #autoplayTimer = null;

  /** @type {number} */
  #autoplayMs = 6000;

  /** @type {boolean} */
  #autoplay = true;

  /** @type {boolean} */
  #userPaused = false;

  /** @type {boolean} */
  #suspended = false;

  /** @type {number | null} */
  #touchStartX = null;

  /** @type {number | null} */
  #touchStartY = null;

  /** @type {number | null} */
  #pointerId = null;

  /** @type {boolean} */
  #touchIgnore = false;

  /** @type {HTMLElement | null} */
  #swipeSurface = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLButtonElement | null} */
  #autoplayToggle = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {boolean} */
  #inView = true;

  /** @type {WeakMap<HTMLVideoElement, boolean>} */
  #videoPrepared = new WeakMap();

  /** @type {MediaQueryList | null} */
  #reducedMotionMq = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-zoh-banner-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-zoh-banner-dot]'));
    this.#status = this.querySelector('[data-zoh-banner-status]');
    this.#autoplayToggle = this.querySelector('[data-zoh-banner-autoplay-toggle]');

    const autoplayAttr = this.getAttribute('data-autoplay');
    this.#autoplay = autoplayAttr !== 'false';

    const speed = Number.parseInt(this.getAttribute('data-autoplay-speed') || '', 10);
    if (Number.isFinite(speed) && speed >= 2000) this.#autoplayMs = speed;

    this.dataset.slideCount = String(this.#slides.length);
    this.classList.toggle('zoh-banner--single', this.#slides.length < 2);

    if (this.#slides.length === 0) return;

    this.#reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotionMq.addEventListener?.('change', this.#onReducedMotionChange);

    this.#prepareAllVideos();

    this.querySelector('[data-zoh-banner-prev]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.goTo(this.#index - 1, { user: true });
    });
    this.querySelector('[data-zoh-banner-next]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.goTo(this.#index + 1, { user: true });
    });

    this.#dots.forEach((dot, i) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(i, { user: true });
      });
    });

    this.#autoplayToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#setUserPaused(!this.#userPaused);
    });

    this.querySelectorAll('[data-zoh-banner-video-play]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const slide = btn.closest('[data-zoh-banner-slide]');
        if (!(slide instanceof HTMLElement)) return;
        const video = slide.querySelector('video');
        if (!(video instanceof HTMLVideoElement)) return;
        this.#hidePlayFallback(slide);
        void this.#playVideo(video, { force: true });
      });
    });

    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('mouseenter', this.#onMouseEnter);
    this.addEventListener('mouseleave', this.#onMouseLeave);
    document.addEventListener('visibilitychange', this.#onVisibility);

    const viewport = this.querySelector('[data-zoh-banner-viewport]');
    this.#swipeSurface = viewport instanceof HTMLElement ? viewport : this;
    this.#swipeSurface.classList.add('zoh-banner__swipe-surface');
    this.#swipeSurface.addEventListener('pointerdown', this.#onPointerDown);
    this.#swipeSurface.addEventListener('pointermove', this.#onPointerMove, { passive: false });
    this.#swipeSurface.addEventListener('pointerup', this.#onPointerUp);
    this.#swipeSurface.addEventListener('pointercancel', this.#onPointerCancel);
    this.#swipeSurface.addEventListener('lostpointercapture', this.#onPointerCancel);

    if (typeof IntersectionObserver !== 'undefined') {
      this.#visibilityObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.#inView = entry.isIntersecting;
          if (this.#inView) {
            this.#syncActiveMedia(true);
            this.#resumeAutoplay();
          } else {
            this.#syncActiveMedia(false);
            this.#pauseAutoplay();
          }
        },
        { threshold: 0.25 }
      );
      this.#visibilityObserver.observe(this);
    }

    if (this.#prefersReducedMotion()) {
      this.#setUserPaused(true, { silent: true });
    }

    this.goTo(0, { user: false, announce: false });
    this.#resumeAutoplay();
    this.#syncAutoplayButton();
  }

  disconnectedCallback() {
    this.#pauseAutoplay();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#reducedMotionMq?.removeEventListener?.('change', this.#onReducedMotionChange);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('mouseenter', this.#onMouseEnter);
    this.removeEventListener('mouseleave', this.#onMouseLeave);
    document.removeEventListener('visibilitychange', this.#onVisibility);

    if (this.#swipeSurface) {
      this.#swipeSurface.removeEventListener('pointerdown', this.#onPointerDown);
      this.#swipeSurface.removeEventListener('pointermove', this.#onPointerMove);
      this.#swipeSurface.removeEventListener('pointerup', this.#onPointerUp);
      this.#swipeSurface.removeEventListener('pointercancel', this.#onPointerCancel);
      this.#swipeSurface.removeEventListener('lostpointercapture', this.#onPointerCancel);
      this.#swipeSurface = null;
    }
  }

  /**
   * @param {number} next
   * @param {{ user?: boolean, announce?: boolean }} [options]
   */
  goTo(next, options = {}) {
    if (this.#slides.length === 0) return;

    const total = this.#slides.length;
    const index = ((next % total) + total) % total;
    this.#index = index;
    const announce = options.announce !== false;

    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });

    this.#dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    if (announce) this.#announceSlide();

    // Wait a frame so Safari sees the slide as visible before play().
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.#slides.forEach((slide, i) => {
          this.#syncSlideMedia(slide, i === this.#index && this.#inView && !document.hidden);
        });
      });
    });

    if (options.user) this.#resumeAutoplay();
  }

  #announceSlide() {
    if (!(this.#status instanceof HTMLElement)) return;
    const slide = this.#slides[this.#index];
    const label = slide?.getAttribute('aria-label');
    if (!label) return;
    // Retrigger polite live region for repeated identical announcements.
    this.#status.textContent = '';
    window.requestAnimationFrame(() => {
      if (this.#status) this.#status.textContent = label;
    });
  }

  /**
   * @param {boolean} paused
   * @param {{ silent?: boolean }} [options]
   */
  #setUserPaused(paused, options = {}) {
    this.#userPaused = paused;
    this.toggleAttribute('data-paused', paused);
    this.#syncAutoplayButton();
    if (paused) {
      this.#pauseAutoplay();
      this.#syncActiveMedia(false);
    } else if (!options.silent) {
      this.#syncActiveMedia(this.#inView && !document.hidden);
      this.#resumeAutoplay();
    }
  }

  #syncAutoplayButton() {
    const btn = this.#autoplayToggle;
    if (!(btn instanceof HTMLButtonElement)) return;

    const pauseLabel = btn.dataset.labelPause || 'Pause slideshow';
    const playLabel = btn.dataset.labelPlay || 'Play slideshow';
    const paused = this.#userPaused || this.#prefersReducedMotion();

    btn.setAttribute('aria-label', paused ? playLabel : pauseLabel);
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false');

    const pauseIcon = btn.querySelector('.zoh-banner__autoplay-icon--pause');
    const playIcon = btn.querySelector('.zoh-banner__autoplay-icon--play');
    if (pauseIcon instanceof HTMLElement) pauseIcon.hidden = paused;
    if (playIcon instanceof HTMLElement) playIcon.hidden = !paused;
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #onReducedMotionChange = () => {
    if (this.#prefersReducedMotion()) {
      this.#setUserPaused(true);
    } else {
      this.#syncAutoplayButton();
      this.#resumeAutoplay();
    }
  };

  #prepareAllVideos() {
    this.querySelectorAll('video.zoh-banner__video, video').forEach((node) => {
      if (!(node instanceof HTMLVideoElement)) return;
      this.#prepareVideoElement(node);
    });
  }

  /**
   * Safari requires muted + playsinline attributes (not only properties).
   * @param {HTMLVideoElement} video
   */
  #prepareVideoElement(video) {
    if (this.#videoPrepared.get(video)) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.autoplay = false;
    video.controls = false;
    video.tabIndex = -1;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('x-webkit-airplay', 'deny');
    video.setAttribute('aria-hidden', 'true');
    if (!video.getAttribute('preload') || video.getAttribute('preload') === 'none') {
      video.preload = 'metadata';
    }

    this.#videoPrepared.set(video, true);
  }

  /**
   * @param {HTMLVideoElement} video
   * @returns {Promise<void>}
   */
  #waitForCanPlay(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        video.removeEventListener('loadeddata', done);
        video.removeEventListener('canplay', done);
        window.clearTimeout(timeoutId);
        resolve();
      };
      const timeoutId = window.setTimeout(done, 8000);
      video.addEventListener('loadeddata', done, { once: true });
      video.addEventListener('canplay', done, { once: true });

      try {
        video.load();
      } catch {
        // ignore
      }
    });
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {{ force?: boolean }} [options]
   */
  async #playVideo(video, options = {}) {
    if (this.#prefersReducedMotion() && !options.force) return;
    if (this.#userPaused && !options.force) return;

    this.#prepareVideoElement(video);

    const slide = video.closest('[data-zoh-banner-slide]');
    video.preload = 'auto';

    try {
      await this.#waitForCanPlay(video);
      // Re-assert mute right before play — Safari can drop it after load().
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');

      const playPromise = video.play();
      if (playPromise !== undefined) await playPromise;
      if (slide instanceof HTMLElement) {
        this.#hidePlayFallback(slide);
        const wrap = slide.querySelector('.zoh-banner__video-wrap');
        wrap?.setAttribute('data-playing', '');
      }
    } catch {
      // One retry after a short delay (common Safari race).
      await new Promise((resolve) => window.setTimeout(resolve, 200));
      try {
        video.muted = true;
        video.setAttribute('muted', '');
        video.playsInline = true;
        await video.play();
        if (slide instanceof HTMLElement) {
          this.#hidePlayFallback(slide);
          const wrap = slide.querySelector('.zoh-banner__video-wrap');
          wrap?.setAttribute('data-playing', '');
        }
      } catch {
        if (options.force) return;
        if (slide instanceof HTMLElement) this.#showPlayFallback(slide);
      }
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #pauseVideo(video) {
    try {
      video.pause();
    } catch {
      // ignore
    }
    const wrap = video.closest('.zoh-banner__video-wrap');
    wrap?.removeAttribute('data-playing');
  }

  /**
   * @param {HTMLElement} slide
   */
  #showPlayFallback(slide) {
    const btn = slide.querySelector('[data-zoh-banner-video-play]');
    if (btn instanceof HTMLElement) btn.hidden = false;
  }

  /**
   * @param {HTMLElement} slide
   */
  #hidePlayFallback(slide) {
    const btn = slide.querySelector('[data-zoh-banner-video-play]');
    if (btn instanceof HTMLElement) btn.hidden = true;
  }

  /**
   * @param {boolean} shouldPlay
   */
  #syncActiveMedia(shouldPlay) {
    this.#slides.forEach((slide, i) => {
      this.#syncSlideMedia(slide, shouldPlay && i === this.#index && !this.#userPaused);
    });
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} active
   */
  #syncSlideMedia(slide, active) {
    const video = slide.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      if (active) void this.#playVideo(video);
      else this.#pauseVideo(video);
    }

    slide.querySelectorAll('iframe[data-zoh-banner-embed]').forEach((frame) => {
      if (!(frame instanceof HTMLIFrameElement)) return;
      this.#syncEmbed(frame, active);
    });
  }

  /**
   * @param {HTMLIFrameElement} frame
   * @param {boolean} active
   */
  #syncEmbed(frame, active) {
    const type = frame.dataset.videoType;

    const intended = frame.dataset.embedSrc || frame.getAttribute('src') || '';
    if (intended && intended !== 'about:blank') {
      frame.dataset.embedSrc = intended;
      if (active && (!frame.src || frame.src === 'about:blank')) {
        frame.src = intended;
      }
    }

    window.setTimeout(() => {
      try {
        if (type === 'youtube') {
          frame.contentWindow?.postMessage(
            JSON.stringify({
              event: 'command',
              func: active ? 'playVideo' : 'pauseVideo',
              args: [],
            }),
            '*'
          );
          if (active) {
            frame.contentWindow?.postMessage(
              JSON.stringify({ event: 'command', func: 'mute', args: [] }),
              '*'
            );
          }
        } else if (type === 'vimeo') {
          frame.contentWindow?.postMessage(
            JSON.stringify({ method: active ? 'play' : 'pause' }),
            '*'
          );
          if (active) {
            frame.contentWindow?.postMessage(
              JSON.stringify({ method: 'setVolume', value: 0 }),
              '*'
            );
          }
        }
      } catch {
        // Cross-origin postMessage failures are non-fatal.
      }
    }, active ? 250 : 0);
  }

  #pauseAutoplay = () => {
    if (this.#autoplayTimer !== null) {
      window.clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  };

  #resumeAutoplay = () => {
    this.#pauseAutoplay();
    if (!this.#autoplay || this.#slides.length < 2) return;
    if (this.#userPaused || this.#suspended) return;
    if (this.#prefersReducedMotion()) return;
    if (document.hidden || !this.#inView) return;

    this.#autoplayTimer = window.setInterval(() => {
      this.goTo(this.#index + 1);
    }, this.#autoplayMs);
  };

  #onVisibility = () => {
    if (document.hidden) {
      this.#pauseAutoplay();
      this.#syncActiveMedia(false);
      return;
    }
    this.#syncActiveMedia(this.#inView);
    this.#resumeAutoplay();
  };

  #onFocusIn = () => {
    this.#suspended = true;
    this.#pauseAutoplay();
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#suspended = false;
    this.#resumeAutoplay();
  };

  #onMouseEnter = () => {
    this.#suspended = true;
    this.#pauseAutoplay();
  };

  #onMouseLeave = () => {
    if (this.contains(document.activeElement)) return;
    this.#suspended = false;
    this.#resumeAutoplay();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (this.#slides.length < 2) return;

    const target = event.target;
    const inEditable =
      target instanceof HTMLElement &&
      target.closest('input, textarea, select, [contenteditable="true"]');
    if (inEditable) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.#index - 1, { user: true });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.#index + 1, { user: true });
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.goTo(0, { user: true });
    } else if (event.key === 'End') {
      event.preventDefault();
      this.goTo(this.#slides.length - 1, { user: true });
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (this.#slides.length < 2) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (
      event.target instanceof Element &&
      event.target.closest('a, button, input, textarea, select, label, .zoh-banner__controls')
    ) {
      this.#touchIgnore = true;
      this.#resetSwipe();
      return;
    }

    this.#touchIgnore = false;
    this.#touchStartX = event.clientX;
    this.#touchStartY = event.clientY;
    this.#pointerId = event.pointerId;

    const surface = this.#swipeSurface;
    if (surface && typeof surface.setPointerCapture === 'function') {
      try {
        surface.setPointerCapture(event.pointerId);
      } catch {
        // Some browsers reject capture on certain pointer types.
      }
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#touchIgnore || this.#touchStartX === null || this.#touchStartY === null) return;
    if (this.#pointerId !== null && event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#touchStartX;
    const dy = event.clientY - this.#touchStartY;

    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.1) {
      event.preventDefault();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#touchIgnore) {
      this.#touchIgnore = false;
      this.#resetSwipe();
      return;
    }
    if (this.#touchStartX === null || this.#touchStartY === null) return;
    if (this.#pointerId !== null && event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#touchStartX;
    const dy = event.clientY - this.#touchStartY;
    this.#resetSwipe();

    if (Math.abs(dx) < 40) return;
    if (Math.abs(dx) < Math.abs(dy)) return;

    this.goTo(this.#index + (dx < 0 ? 1 : -1), { user: true });
  };

  #onPointerCancel = () => {
    this.#touchIgnore = false;
    this.#resetSwipe();
  };

  #resetSwipe() {
    this.#touchStartX = null;
    this.#touchStartY = null;
    this.#pointerId = null;
  }
}

if (!customElements.get('zoh-banner')) {
  customElements.define('zoh-banner', ZohBanner);
}
